import type { Ref } from 'react';
import { createContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { DialogProps as AriaDialogProps, DialogTriggerProps } from 'react-aria-components/Dialog';
import { Dialog as AriaDialog, DialogTrigger } from 'react-aria-components/Dialog';
import type { ContextValue } from 'react-aria-components/slots';
import { Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import { useSlotId } from '@react-aria/utils';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/Dialog.module.css';

const dialogStyles = cva(styles.dialog);

interface DialogProps extends AriaDialogProps {
	ref?: Ref<HTMLElement>;
}

const DialogContext = createContext<ContextValue<DialogProps, HTMLElement>>(null);

/**
 * A dialog is an overlay shown above other content in an application.
 *
 * https://react-spectrum.adobe.com/react-aria/Dialog.html
 */
const Dialog = ({ ref, ...props }: DialogProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, DialogContext);
	const { className } = mergedProps;
	const descriptionId = useSlotId();

	return (
		<AriaDialog
			{...mergedProps}
			ref={mergedRef}
			className={dialogStyles({ className })}
			aria-describedby={mergedProps['aria-describedby'] || descriptionId}
		>
			{composeRenderProps(mergedProps.children, (children) => (
				<Provider
					values={[
						[
							TextContext,
							{
								slots: {
									subtitle: { id: descriptionId, elementType: 'h3' },
								},
							},
						],
					]}
				>
					{children}
				</Provider>
			))}
		</AriaDialog>
	);
};

export { Dialog, DialogContext, DialogTrigger, dialogStyles };
export type { DialogProps, DialogTriggerProps };
